/**
 * confirmation-pages-api — the persistence seam for Confirmation Page documents
 * (Journeys → Confirmation pages tab, route `confirmpages`), ON THE CONTRACT.
 *
 * Written by the API side as the implementation spec for the swap (2026-09-04),
 * against the typed client that shipped in the kit. Paste over the stubbed
 * module; every exported name and its return shape is kept, with ONE change
 * that is the whole point: every function is now `async`. The typed client
 * returns promises carrying the parsed response, so `reload()` and the six
 * handlers that call-then-reload become `await`s with a real error path.
 *
 * WHAT MAPS TO WHAT. The contract's document (`ConfirmationPageDto`) names only
 * what the API reasons about — `pageId`, `name`, `isDefault`, `status`,
 * `updatedAt`, `sections`, `serviceIds`, `branding`, `cancellationWindow`,
 * `content`, `heading`, `subheading`. Everything else on `CPDoc` — `applies`,
 * `sub`, `note`, `bannerTitle`, `edited` — rides in the document's open
 * interior: stored verbatim, returned verbatim. So `toCPDoc`/`toPutInput`
 * below rename the owned fields and pass the interior straight through.
 *
 * TWO FIELDS ARE SERVER-OWNED and ignored on write, never rejected: `isDefault`
 * and `serviceIds`. Send them or not; the server decides both.
 *
 * THE DEFAULT IS BOOTSTRAPPED SERVER-SIDE on first read, so `listConfirmationPages`
 * never returns an empty list. The `pages.length === 0 → seedDefault()` branch in
 * the screen's `reload()` is therefore dead — and if it ever ran it would create
 * a NON-default page named "Default confirmation page", because `isDefault` on a
 * write is ignored. Delete that branch.
 *
 * CLONES ARE LIVE, NOT DRAFT. The server copies the source's status and claims
 * no services, so a fresh clone resolves nothing until assigned — inert, not
 * dangerous. The stub made clones Draft. If Draft-on-clone is the intended UX,
 * `putConfirmationPage(slug, { ...c, status: "Draft" })` right after the clone
 * does it in one extra call; say so and we can make the server do it instead.
 *
 * `slug` stays in every signature for source compatibility and is NOT used: the
 * client is tenant-bound by the session, never by an argument.
 */

import {
  isApiError,
  listConfirmationPages as apiList,
  getConfirmationPage as apiGet,
  putConfirmationPage as apiPut,
  cloneConfirmationPage as apiClone,
  deleteConfirmationPage as apiDelete,
  putConfirmationPageServices as apiSetServices,
  resolveConfirmationPage as apiResolve,
} from "@v0y4ge/api-client";
import type { ConfirmationPageDto, ConfirmationPagePutInput } from "@v0y4ge/api-contracts";
import { csMigrate, type CSState } from "./confirmation-sections";

export type CPApplies = "all" | "appointment" | "event" | "queue";
export type CPStatus = "Published" | "Draft";

export interface CPBranding {
  inherit: boolean;
  brandColor: string;
  pageBg: string;
}

export interface CPBlock {
  uid: string;
  type: string;
  text?: string;
  /** Absolute HTTP(S) destination for a labelled link. Absent on legacy
   * URL-in-text blocks, which the renderer continues to understand. */
  href?: string;
  /** WHERE ON THE PAGE this block sits: the number of SECTION rows that come
   *  before it. Absent (and any value past the end) means after them all, which
   *  is where every block authored before 2026-09-14 sat and still sits.
   *
   *  A slot index rather than one merged ordered list of sections-and-blocks:
   *  `sections` is a contract array with its own shape and its own migration,
   *  and folding blocks into it would rewrite both for a placement. Blocks stay
   *  `content[]`, sections stay `sections[]`, and this one number says how they
   *  interleave. The renderer clamps it, so a slot pointing past a section that
   *  was later removed degrades to "at the end" instead of vanishing. */
  at?: number;
  [k: string]: unknown;
}

export interface CPDoc {
  id: string;
  name: string;
  isDefault: boolean;
  applies: CPApplies;
  status: CPStatus;
  sub: string;
  /** ISO timestamp from the server (was "Just now" / "—" on the stub). */
  updated: string;
  sections: CSState;
  branding: CPBranding;
  /** Hours BEFORE THE APPOINTMENT STARTS; "" means no cancellation offered. */
  cancelHours: string;
  /** When MOVE closes, in hours before the start (2026-09-14, operator).
   *  "" means the tenant runs one window and Move follows `cancelHours`.
   *
   *  INTERIOR, NOT CONTRACT — and that is the open question, not an oversight.
   *  `cancellationWindow` is a named contract field because the manage worker
   *  ENFORCES it; this one only reaches the page's wording, so today an author
   *  can promise "move up to 4 hours before" while the server keeps allowing it
   *  to 24. Filed: CD-API-REQUEST-rebooking-window-2026-09-14.md. */
  /** When MOVE closes, in hours before the start. "" means the tenant runs one
   *  window and Move follows `cancelHours`.
   *
   *  CONTRACTED since the platform shipped `rebookingWindow` (2026-09-14):
   *  `null`/absent = follows the cancellation window, `{hours: 0}` = open until
   *  the start. It rode the document interior for one afternoon before that
   *  landed; `toCPDoc` still reads the interior key as a fallback so a page
   *  authored in that window keeps its value, and the next save moves it onto
   *  the real field. */
  rebookHours: string;
  /** Which download items the AUTHOR withholds, by id: `ics` · `wallet` · `pdf`
   *  · `export`. Absent id = offered.
   *
   *  It can only ever take away. The server flags decide whether an item is
   *  possible at all (`summaryPdf`, `walletPass`, the session's `canPdf` /
   *  `dataExport`); this says whether a tenant that COULD offer it wants to.
   *  Author-on plus server-off is still absent — the page never argues with the
   *  server about what exists. */
  downloadsOff: string[];
  /** The "What to bring" checklist, one string per line. Interior, like `note`.
   *  The customer's page has always READ this (`page.prepare`); nothing wrote
   *  it until 2026-09-14, so every real page rendered the section's empty state
   *  while the designer showed a hardcoded sample. */
  prepare: string[];
  /** Banner sub-line, plain-confirmation only — same rule as `bannerTitle`. */
  bannerSub: string;
  /** Booking-summary rows the author withholds, by stable key (see
   *  `SUMMARY_ROWS`): `service` · `date` · `time` · `duration` · `advisor` ·
   *  `event` · `venue` · `tickets` · `ticket` · `where`. Keys, not labels: the
   *  labels differ per journey type and will be translated. */
  summaryOff: string[];
  /** Per-row label overrides for the booking summary, key → label. A key absent
   *  (or emptied) means the registry's default; "" is never stored, because an
   *  empty override would print a blank row title. */
  summaryLabels: Record<string, string>;
  /** The author's row order, by key. Keys this journey type does not have are
   *  ignored, and rows the order does not mention are appended in registry
   *  order — so a page authored before a row existed keeps it. */
  summaryOrder: string[];
  /** Which date shape the summary prints — an id from `SUMMARY_DATE_FORMATS`.
   *  `auto` (the default) follows the reader's device; every other id carries
   *  its own locale, so “DE” stays German for a reader on en-US. */
  summaryDateFormat: string;
  /** Clock shape for the Time row: `auto` · `h24` · `h12`. */
  summaryTimeFormat: string;
  /** What the check-in QR encodes: `reference` (staff scan it on My Day) or
   *  `manageUrl` (a customer's phone camera opens it). Presentation only — no
   *  server behaviour hangs off it, so interior is the right home. */
  checkinCodeTarget: "reference" | "manageUrl";
  note: string;
  bannerTitle: string;
  content?: CPBlock[];
  heading?: string;
  subheading?: string;
  edited?: boolean;
  __source?: string;
}

const SOURCE = "api";

/* ── mapping ──────────────────────────────────────────────────────────── */

/** Contract document → screen document. The interior keys come through the
 *  passthrough, so they are read off the same object under their own names. */
function toCPDoc(dto: ConfirmationPageDto): CPDoc {
  const x = dto as Record<string, unknown>;
  const applies = (x.applies as CPApplies | undefined) ?? "all";
  const type = applies === "event" ? "event" : applies === "queue" ? "queue" : "appointment";
  const hours = dto.cancellationWindow?.hours;
  const rebookHrs = (dto as { rebookingWindow?: { hours?: number } | null }).rebookingWindow?.hours;
  return {
    id: dto.pageId,
    name: dto.name,
    isDefault: dto.isDefault === true,
    applies,
    status: dto.status === "draft" ? "Draft" : "Published",
    sub: (x.sub as string | undefined) ?? "",
    updated: dto.updatedAt ?? "",
    /* csMigrate is idempotent; the contract's shape IS CSState, so this is a
       no-op on every document the API has stored, and still closes the legacy
       {order, off} case for anything imported from the stub era. */
    sections: csMigrate(dto.sections, type, "linked"),
    // Historical page-level overrides are intentionally ignored. Customer
    // surfaces and previews resolve identity from the canonical Brand Kit.
    branding: { inherit: true, brandColor: "#5142C7", pageBg: "#FFFFFF" },
    cancelHours: typeof hours === "number" ? String(hours) : "",
    rebookHours: typeof rebookHrs === "number" ? String(rebookHrs) : ((x.rebookHours as string | undefined) ?? ""),
    downloadsOff: Array.isArray(x.downloadsOff) ? (x.downloadsOff as string[]) : [],
    prepare: Array.isArray(x.prepare)
      ? (x.prepare as unknown[]).filter((l): l is string => typeof l === "string" && l.trim() !== "")
      : [],
    bannerSub: (x.bannerSub as string | undefined) ?? "",
    summaryOff: Array.isArray(x.summaryOff) ? (x.summaryOff as string[]) : [],
    summaryLabels: (x.summaryLabels && typeof x.summaryLabels === "object" ? x.summaryLabels : {}) as Record<string, string>,
    summaryOrder: Array.isArray(x.summaryOrder) ? (x.summaryOrder as string[]) : [],
    summaryDateFormat: (x.summaryDateFormat as string | undefined) ?? "auto",
    summaryTimeFormat: (x.summaryTimeFormat as string | undefined) ?? "auto",
    checkinCodeTarget: x.checkinCodeTarget === "manageUrl" ? "manageUrl" : "reference",
    note: (x.note as string | undefined) ?? "",
    bannerTitle: (x.bannerTitle as string | undefined) ?? "",
    content: dto.content as CPBlock[] | undefined,
    heading: dto.heading,
    subheading: dto.subheading,
    edited: x.edited as boolean | undefined,
    __source: SOURCE,
  };
}

/** Screen document → contract write. `id`, `isDefault`, `updated`, `__source`
 *  are dropped: the first is the path, the second is server-owned, the last two
 *  are read-only. Everything the contract does not name goes as interior. */
export function toPutInput(doc: CPDoc): ConfirmationPagePutInput {
  const hours = Number(doc.cancelHours);
  const rebookHours = Number(doc.rebookHours);
  return {
    name: doc.name,
    status: doc.status === "Draft" ? "draft" : "live",
    sections: doc.sections,
    // "" or a non-number → null → "this page offers no cancellation affordance".
    cancellationWindow: doc.cancelHours.trim() !== "" && Number.isFinite(hours) && hours >= 0 ? { hours } : null,
    /* Same shape and the same nullability as the cancellation window: null
       means "follows cancellation", `{hours: 0}` means "open until the start".
       Which is why an empty string maps to null and a typed 0 does not. */
    rebookingWindow: doc.rebookHours.trim() !== "" && Number.isFinite(rebookHours) && rebookHours >= 0 ? { hours: rebookHours } : null,
    content: doc.content,
    heading: doc.heading,
    subheading: doc.subheading,
    // Interior — preserved verbatim by the store, round-trips untouched.
    applies: doc.applies,
    sub: doc.sub,
    note: doc.note,
    downloadsOff: doc.downloadsOff,
    prepare: doc.prepare,
    bannerSub: doc.bannerSub,
    summaryOff: doc.summaryOff,
    summaryLabels: doc.summaryLabels,
    summaryOrder: doc.summaryOrder,
    summaryDateFormat: doc.summaryDateFormat,
    summaryTimeFormat: doc.summaryTimeFormat,
    checkinCodeTarget: doc.checkinCodeTarget,
    bannerTitle: doc.bannerTitle,
    edited: doc.edited,
  } as ConfirmationPagePutInput;
}

/* ── the seven ops ────────────────────────────────────────────────────── */

/** cap: GET /v1/admin/confirmation-pages */
export async function listConfirmationPages(_slug: string): Promise<{ pages: CPDoc[]; assignments: Record<string, string>; stubbed: boolean }> {
  const res = await apiList();
  // Server order is already default-first, then name. Kept here so the screen's
  // expectation does not depend on it.
  const pages = res.data.map(toCPDoc).sort((a, b) => (a.isDefault === b.isDefault ? a.name.localeCompare(b.name) : a.isDefault ? -1 : 1));
  return { pages, assignments: res.assignments, stubbed: CP_STUBBED };
}

/** cap: GET /v1/admin/confirmation-pages/{pageId} */
export async function getConfirmationPage(_slug: string, id: string): Promise<CPDoc | null> {
  try {
    return toCPDoc((await apiGet(id)).data);
  } catch (e) {
    if (isApiError(e) && e.code === "CONFIRMATION_PAGE_NOT_FOUND") return null;
    throw e;
  }
}

/** cap: PUT /v1/admin/confirmation-pages/{pageId} — create or replace. */
export async function putConfirmationPage(_slug: string, doc: CPDoc): Promise<CPDoc> {
  return toCPDoc((await apiPut(doc.id, toPutInput(doc))).data);
}

/** cap: POST /v1/admin/confirmation-pages/{pageId}/clone
 *  The client mints the Idempotency-Key the op requires; a double-tap replays
 *  the first clone rather than minting a second. */
export async function cloneConfirmationPage(_slug: string, sourceId: string): Promise<CPDoc | null> {
  try {
    return toCPDoc((await apiClone(sourceId)).data);
  } catch (e) {
    if (isApiError(e) && e.code === "CONFIRMATION_PAGE_NOT_FOUND") return null;
    throw e;
  }
}

/** cap: DELETE /v1/admin/confirmation-pages/{pageId}
 *  The default's refusal is server-side (409); the local guard in the screen is
 *  now belt and braces. */
export async function deleteConfirmationPage(_slug: string, id: string): Promise<{ ok: boolean; reason?: string }> {
  try {
    await apiDelete(id);
    return { ok: true };
  } catch (e) {
    if (isApiError(e) && e.code === "CONFIRMATION_PAGE_DEFAULT_CONFLICT") return { ok: false, reason: "default-page-is-structural" };
    if (isApiError(e) && e.code === "CONFIRMATION_PAGE_NOT_FOUND") return { ok: false, reason: "not-found" };
    throw e;
  }
}

/** cap: PUT /v1/admin/confirmation-pages/{pageId}/services
 *  `moved` carries `fromPageName`, so "Moved from Standard confirmation" needs
 *  no local lookup; `stolenFrom` is kept for the existing call site. */
export async function setPageServices(_slug: string, pageId: string, serviceIds: string[]): Promise<{
  stolenFrom: string[];
  moved: { serviceId: string; fromPageId: string; fromPageName: string }[];
  assignments: Record<string, string>;
}> {
  const res = await apiSetServices(pageId, serviceIds);
  return { stolenFrom: [...new Set(res.moved.map((m) => m.fromPageId))], moved: res.moved, assignments: res.assignments };
}

/** cap: GET /v1/admin/confirmation-pages/resolve?serviceId=…
 *  THE runtime rule, answered by the server. For the library's list FILTER use
 *  `resolveLocally` below — the filter runs per row and must stay synchronous. */
export async function resolveForService(_slug: string, serviceId: string): Promise<CPDoc | null> {
  try {
    return toCPDoc((await apiResolve(serviceId)).data);
  } catch (e) {
    if (isApiError(e) && e.code === "CONFIRMATION_PAGE_UNAVAILABLE") return null;
    throw e;
  }
}

/** The same rule, computed from the list the screen already holds. Identical
 *  to the server's answer by construction — `assignments` is the one source of
 *  truth on both sides — and synchronous, which the list filter needs. */
export function resolveLocally(pages: CPDoc[], assignments: Record<string, string>, serviceId: string): CPDoc | null {
  const claimed = assignments[serviceId];
  const page = claimed ? pages.find((p) => p.id === claimed && p.status !== "Draft") : undefined;
  return page ?? pages.find((p) => p.isDefault) ?? null;
}

/** The public lane is NOT this module's concern any more: the customer surface
 *  (`src/manage`) calls `getPublicBookingConfirmationPage(bookingId)` directly
 *  and maps the response with `confirmation-page-view.ts`. `resolveForBooking`
 *  is removed rather than kept as a misleading alias. */

/** False the moment this module is in the tree. Left as a constant so the
 *  provenance note's call site compiles; delete both when convenient. */
export const CP_STUBBED = false;
